import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './Button';
import { getConsent, setConsent } from '../../services/analytics.service';
import { onConsentGranted } from '../../services/eventTracking.service';
import styles from '../../styles/components/CookieConsent.module.css';

/**
 * Cookie-Consent-Banner mit Consent Mode v2.
 *
 * Wird nur angezeigt, wenn noch keine Decision (kein `ts`) im LocalStorage steht.
 * Mobile-First.
 */
function CookieConsent() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Während des react-snap Prerenders nicht anzeigen, sonst entsteht beim
    // Hydrate ein Mismatch (Banner im HTML, aber initialer Client-Render = null
    // wegen useState(false)) → React Error #423.
    if (typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent)) {
      return;
    }
    const consent = getConsent();
    setVisible(typeof consent.ts !== 'number');
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setConsent({ analytics: true });
    onConsentGranted();
    setVisible(false);
  };

  const acceptNecessary = () => {
    setConsent({ analytics: false });
    setVisible(false);
  };

  return (
    <div
      className={styles.banner}
      role="dialog"
      aria-live="polite"
      aria-label={t('cookieConsent.ariaLabel')}
    >
      <div className={styles.inner}>
        <h2 className={styles.title}>{t('cookieConsent.title')}</h2>
        <p className={styles.text}>
          {t('cookieConsent.text')}{' '}
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
            {t('cookieConsent.privacyLink')}
          </a>
          .
        </p>

        {showDetails && (
          <div className={styles.details}>
            <div className={styles.detailsRow}>
              <strong>{t('cookieConsent.detailsNecessaryTitle')}</strong>
              <span>{t('cookieConsent.detailsNecessaryText')}</span>
            </div>
            <div className={styles.detailsRow}>
              <strong>{t('cookieConsent.detailsStatsTitle')}</strong>
              <span>{t('cookieConsent.detailsStatsText')}</span>
            </div>
          </div>
        )}

        <button
          type="button"
          className={styles.toggleBtn}
          onClick={() => setShowDetails((v) => !v)}
        >
          {showDetails ? t('cookieConsent.hideSettings') : t('cookieConsent.showSettings')}
        </button>

        {/* Ablehnen und Zustimmen müssen gleichwertig sein: gleiche Größe,
            gleiches Gewicht, nebeneinander. Vorher war „Nur essenzielle" ein
            ghost-Button neben einem blauen primary — das ist die Gestaltung,
            die Aufsichtsbehörden als unzulässige Nudging-Praxis werten
            (§ 25 TDDDG i.V.m. Art. 4 Nr. 11 DSGVO, DSK-Orientierungshilfe). */}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={acceptNecessary} fullWidth>
            {t('cookieConsent.acceptNecessary')}
          </Button>
          <Button variant="primary" onClick={acceptAll} fullWidth>
            {t('cookieConsent.acceptAll')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
